import React, { useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { IconHexagonNumber1Filled, IconHexagonNumber2Filled, IconHexagonNumber3Filled, IconHexagonNumber4Filled, IconHexagonNumber5Filled, IconHexagonNumber6Filled, IconClockHour9, IconCircleCheck } from '@tabler/icons-react';
import { instrumentalProject, siftedFeaturedProject, navigableProjects } from '../data/projects';
import { Project } from '../data/projects';
import './ProjectPage.css';
import { usePassword } from '../contexts/PasswordContext';
import PasswordPrompt from '../components/PasswordPrompt';

// Map of featured project IDs to their data
const featuredProjects: { [key: string]: Project } = {
  'instrumental': instrumentalProject,
  'sifted-featured': siftedFeaturedProject,
};

const FeaturedProjectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = usePassword();
  const location = useLocation();
  
  // Extract project ID from pathname since we have static routes
  const getProjectIdFromPath = () => {
    if (location.pathname === '/project/instrumental') return 'instrumental';
    if (location.pathname === '/project/sifted-featured') return 'sifted-featured';
    return id || 'instrumental';
  };
  
  const projectId = getProjectIdFromPath();
  const project = featuredProjects[projectId];
  
  // Debug logging
  console.log('URL id:', id);
  console.log('Pathname:', location.pathname);
  console.log('Project ID:', projectId);
  console.log('Available projects:', Object.keys(featuredProjects));
  console.log('Selected project title:', project?.title);

  // Find current project index and get previous/next projects from navigable projects with circular navigation
  const currentIndex = navigableProjects.findIndex(p => p.id === projectId);
  const previousProject = currentIndex !== -1 ? 
    (currentIndex > 0 ? navigableProjects[currentIndex - 1] : navigableProjects[navigableProjects.length - 1]) : 
    null;
  const nextProject = currentIndex !== -1 ? 
    (currentIndex < navigableProjects.length - 1 ? navigableProjects[currentIndex + 1] : navigableProjects[0]) : 
    null;

  // Set document title
  useEffect(() => {
    if (project) {
      document.title = `${project.title} | Zi Wang's portfolio`;
    } else {
      document.title = 'Project Not Found | Zi Wang\'s portfolio';
    }
  }, [project]);

  // Scroll to top when component mounts or project changes
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }, 0);
  }, [location.pathname]);

  if (!isAuthenticated) {
    return <PasswordPrompt />;
  }

  // Handle project not found
  if (!project) {
    return (
      <div className="project-not-found">
        <div className="container">
          <h1>Featured Project Not Found</h1>
          <p>The featured project you're looking for doesn't exist.</p>
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="project-page">
      <div className="container">
        <div className="project-header">
          <h1 className="project-title" dangerouslySetInnerHTML={{ 
            __html: project.tagline || project.title 
          }}></h1>
          {project.caseStudy && project.caseStudy.sections.length >= 4 && (
            <div className="project-meta-row">
              <div className="project-meta-item">
                <h2><IconClockHour9 size={20} /> {project.caseStudy.sections[2].content}</h2>
              </div>
              <div className="project-meta-item">
                <h2><IconCircleCheck size={20} /> {project.caseStudy.sections[3].content}</h2>
              </div>
            </div>
          )}
        </div>

        <div className="project-hero">
          <img src={project.image} alt={project.title} className="project-hero-image" />
        </div>
      </div>

      {project.caseStudy && (
        <>
          {/* First section: The problem (full width) */}
          {project.caseStudy.sections.length >= 1 && (
            <section className="case-study-section-wrapper background-section reduced-spacing">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>Company</h2>
                  <p dangerouslySetInnerHTML={{ 
                    __html: project.caseStudy.sections[0].content?.replace(/\n\n/g, '<br><br>') || ''
                  }}></p>
                </div>
              </div>
            </section>
          )}

          {/* My role section */}
          {project.caseStudy.sections.length >= 2 && (
            <section className="case-study-section-wrapper surface-section reduced-spacing">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>{project.caseStudy.sections[1].title}</h2>
                  <p dangerouslySetInnerHTML={{ 
                    __html: project.caseStudy.sections[1].content?.replace(/\n\n/g, '<br><br>') || ''
                  }}></p>
                </div>
              </div>
            </section>
          )}

          
          {/* Goal & Results section */}
          {project.caseStudy.sections.length >= 5 && (
            <section className="case-study-section-wrapper surface-section reduced-spacing">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>{project.caseStudy.sections[4].title}</h2>
                  {project.caseStudy.sections[4].content && (
                    <p dangerouslySetInnerHTML={{ 
                      __html: project.caseStudy.sections[4].content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                    }}></p>
                  )}
                  {project.caseStudy.sections[4].keyOutcomes && (
                    <div className="key-outcomes-stack">
                      {project.caseStudy.sections[4].keyOutcomes.map((outcome, outcomeIndex) => {
                        const IconComponent = outcomeIndex === 0 ? IconHexagonNumber1Filled : 
                                            outcomeIndex === 1 ? IconHexagonNumber2Filled : 
                                            outcomeIndex === 2 ? IconHexagonNumber3Filled :
                                            IconHexagonNumber4Filled;
                        return (
                          <div key={outcomeIndex} className="vertical-stack-item">
                            <IconComponent size={24} className="vertical-stack-icon" />
                            <div className="vertical-stack-content">
                              <p dangerouslySetInnerHTML={{ 
                                __html: outcome
                                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                              }}></p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Divider between Goal & Results and Visual Correlation Story */}
          <div className="section-divider"></div>
          
          {/* Rest of the sections starting from Visual Correlation Story */}
          {project.caseStudy.sections.slice(5).map((section, index) => (
            <section key={index + 6} className={`case-study-section-wrapper ${index % 2 === 0 ? 'background-section' : 'surface-section'}`}>
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2 id={section.title === "Visual correlation" ? "visual-correlation" : section.title === "Scenario modeling" ? "scenario-modeling" : undefined}>{section.title}</h2>
                  {section.content && (
                    section.title === "The problem" ? (
                      <blockquote>{section.content}</blockquote>
                    ) : (
                      <p dangerouslySetInnerHTML={{ 
                        __html: section.content
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                      }}></p>
                    )
                  )}
                  
                  {/* Display section image if it exists */}
                  {section.solutionImage && (
                    <div className="solution-grid">
                      <div className="solution-step">
                        <div className="solution-step-image">
                          <img 
                            src={section.solutionImage} 
                            alt={`${section.title} visualization`} 
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Handle subsections */}
                  {section.subsections && (
                    <div className="subsections">
                      {section.subsections.map((subsection, subIndex) => (
                        <div key={subIndex} className="subsection">
                          <h3>{subsection.title}</h3>
                          <p dangerouslySetInnerHTML={{ 
                            __html: subsection.content
                              .replace(/\n\n/g, '<br><br>')
                              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                              .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                          }}></p>
                        </div>
                      ))}
                    </div>
                  )}

                  {section.keyOutcomes && (
                    <div className="key-outcomes-grid">
                      {section.keyOutcomes.map((outcome, outcomeIndex) => {
                        const IconComponent = outcomeIndex === 0 ? IconHexagonNumber1Filled : 
                                            outcomeIndex === 1 ? IconHexagonNumber2Filled : 
                                            IconHexagonNumber3Filled;
                        return (
                          <div key={outcomeIndex} className="key-outcome-card">
                            <div className="key-outcome-header">
                              <IconComponent size={24} className="key-outcome-icon" />
                            </div>
                            <p dangerouslySetInnerHTML={{ 
                              __html: outcome
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                            }}></p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {section.solutionItems && (
                    <div className="solution-grid">
                      {section.solutionItems.map((item, itemIndex) => (
                        <div key={itemIndex} className="solution-step">
                          <div className="solution-step-content">
                            <h3>{item}</h3>
                          </div>
                          <div className="solution-step-image">
                            {(() => {
                              const imageData = section.solutionImages?.[itemIndex] || section.solutionImage || "/img/placeholder.png";
                              if (Array.isArray(imageData)) {
                                return (
                                  <div className="solution-image-grid">
                                    {imageData.map((imgSrc, imgIndex) => (
                                      <img 
                                        key={imgIndex}
                                        src={imgSrc} 
                                        alt={`${item} visualization ${imgIndex + 1}`} 
                                      />
                                    ))}
                                  </div>
                                );
                              } else {
                                return (
                                  <img 
                                    src={imageData} 
                                    alt={`${item} visualization`} 
                                  />
                                );
                              }
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>
          ))}

          {/* Research & Discovery - Standalone section with different background */}
          {project.caseStudy.researchDiscovery && (
            <section className="case-study-section-wrapper surface-section">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>Research & Discovery</h2>
                  <p>{project.caseStudy.researchDiscovery}</p>
                  {project.caseStudy.researchTakeaways && (
                    <div className="research-takeaways-grid">
                      {project.caseStudy.researchTakeaways.map((takeaway, takeawayIndex) => {
                        const IconComponent = takeawayIndex === 0 ? IconHexagonNumber1Filled : 
                                            takeawayIndex === 1 ? IconHexagonNumber2Filled : 
                                            takeawayIndex === 2 ? IconHexagonNumber3Filled :
                                            takeawayIndex === 3 ? IconHexagonNumber4Filled :
                                            takeawayIndex === 4 ? IconHexagonNumber5Filled :
                                            IconHexagonNumber6Filled;
                        return (
                          <div key={takeawayIndex} className="key-outcome-card">
                            <div className="key-outcome-header">
                              <IconComponent size={24} className="key-outcome-icon" />
                            </div>
                            <p dangerouslySetInnerHTML={{ 
                              __html: takeaway
                                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--color-accent);">$1</a>')
                            }}></p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {project.caseStudy.userJourneyMapping && (
                    <>
                      <p style={{marginTop: 'calc(var(--space-8) + var(--space-2))'}}>{project.caseStudy.userJourneyMapping}</p>
                    </>
                  )}
                  {project.caseStudy.platformUsageAnalysis && (
                    <>
                      <p style={{marginTop: 'calc(var(--space-8) + var(--space-2))'}}>{project.caseStudy.platformUsageAnalysis}</p>
                    </>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Additional sections: Strategic Pivot, Design Decisions, Validation & Iteration, Lessons Learned */}

          {project.caseStudy.strategicPivot && (
            <section className="case-study-section-wrapper background-section">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>Strategic pivot</h2>
                  <p>{project.caseStudy.strategicPivot}</p>
                </div>
              </div>
            </section>
          )}

          {project.caseStudy.designDecisions && (
            <section className="case-study-section-wrapper surface-section">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>Design decisions</h2>
                  {(() => {
                    const paragraphs = project.caseStudy.designDecisions.split('\n\n');
                    return (
                      <>
                        {/* Design principles description */}
                        <p>{paragraphs[0]}</p>
                        
                        {/* Design principles containers */}
                        {project.caseStudy.designPrinciples && (
                          <div className="design-principles-grid">
                            {project.caseStudy.designPrinciples.map((principle, principleIndex) => {
                              const [title, description] = principle.split(' - ');
                              return (
                                <div key={principleIndex} className="design-principle-card">
                                  <div className="design-principle-title" dangerouslySetInnerHTML={{ 
                                    __html: title.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  }}></div>
                                  <div className="design-principle-description">- {description}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {/* User workflow description */}
                        {paragraphs[1] && (
                          <p style={{marginTop: 'calc(var(--space-8) + var(--space-2))'}}>{paragraphs[1]}</p>
                        )}
                        
                      </>
                    );
                  })()}
                </div>
              </div>
            </section>
          )}

          {project.caseStudy.validationIteration && (
            <section className="case-study-section-wrapper background-section">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>Validation & Iteration</h2>
                  <p>{project.caseStudy.validationIteration}</p>
                </div>
              </div>
            </section>
          )}

          {project.caseStudy.lessonsLearned && (
            <section className="case-study-section-wrapper surface-section">
              <div className="case-study-content-container">
                <div className="case-study-section">
                  <h2>Lessons learned</h2>
                  <p>{project.caseStudy.lessonsLearned}</p>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {(project.githubUrl || project.liveUrl) && (
        <section className="case-study-section-wrapper background-section">
          <div className="case-study-content-container">
            <div className="project-links">
              <h2>Links</h2>
              <div className="link-buttons">
                {project.githubUrl && (
                  <a 
                    href={project.githubUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="project-link github"
                  >
                    🐙 View on GitHub
                  </a>
                )}
                {project.liveUrl && (
                  <a 
                    href={project.liveUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="project-link live"
                  >
                    🌐 Live Demo
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="project-navigation surface-section">
        <div className="content-max-width" style={{padding: '0 var(--space-4)'}}>
          <div className="navigation-wrapper">
            <div className="nav-item">
              {previousProject && (
                <Link to={
                  previousProject.id === 'instrumental' ? `/project/instrumental` : 
                  previousProject.id === 'sifted-featured' ? `/project/sifted-featured` :
                  `/project/${previousProject.id}`
                } className="nav-link prev">
                  <span className="nav-direction">← Previous</span>
                  <span className="nav-title">{previousProject.title}</span>
                </Link>
              )}
            </div>
            
            <div className="nav-item center">
              <Link to="/" className="back-home-button">Back to home</Link>
            </div>
            
            <div className="nav-item">
              {nextProject && (
                <Link to={
                  nextProject.id === 'instrumental' ? `/project/instrumental` : 
                  nextProject.id === 'sifted-featured' ? `/project/sifted-featured` :
                  `/project/${nextProject.id}`
                } className="nav-link next">
                  <span className="nav-direction">Next →</span>
                  <span className="nav-title">{nextProject.title}</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default FeaturedProjectPage;